export const MONETIZATION_CONTRACT_VERSION = 'free-first-v1';
export const FIRST_VALUE_EVENT = 'first_record_completed';
export const PERMANENT_FREE_CORE_RIGHTS = [
    'first_pet_profile',
    'all_local_health_record_types',
    'local_history_and_trends',
    'medication_schedule_and_log',
    'local_backup_and_recovery',
    'csv_json_export_import',
    'trash_undo_and_archive',
];
/**
 * The current public product exposes these without payment. Before a future activation, the
 * provider ledger must snapshot the cohort so activation cannot take already-free access away.
 */
export const INITIAL_PUBLIC_FREE_COHORT_RIGHTS = [
    'multiple_pet_profiles',
    'report_pdf_generation',
    'caregiver_sharing',
    'encrypted_cloud_backup',
    'full_frequency_notifications',
];
const POST_VALUE_PAYMENT_PLACEMENTS = new Set([
    'second_pet_intent',
    'report_export_intent',
    'caregiver_invite_intent',
    'cloud_backup_intent',
]);
export function digitalCommerceRouteForChannel(channel) {
    switch (channel) {
        case 'apps_in_toss':
            return 'apps_in_toss_iap';
        case 'native_ios':
            return 'apple_storekit';
        case 'native_android':
            return 'google_play_billing';
        default:
            return 'payment_disabled';
    }
}
export function evaluateMonetizationAccess(input) {
    const paymentRoute = digitalCommerceRouteForChannel(input.channel);
    const paymentDecision = evaluatePaymentSurface(input, paymentRoute);
    const advertisingDecision = { visible: false, reason: 'ADVERTISING_EXCLUDED' };
    return Object.freeze({
        contractVersion: MONETIZATION_CONTRACT_VERSION,
        firstValueEvent: FIRST_VALUE_EVENT,
        permanentFreeCoreRights: PERMANENT_FREE_CORE_RIGHTS,
        initialPublicFreeCohortRights: INITIAL_PUBLIC_FREE_COHORT_RIGHTS,
        memberOfInitialPublicFreeCohort: input.memberOfInitialPublicFreeCohort,
        cardRequiredForFreeUse: false,
        trialOffered: false,
        trialAutoConverts: false,
        existingFreeRightsMayBeRevoked: false,
        paymentRoute,
        paywall: paymentDecision,
        checkout: paymentDecision,
        advertising: advertisingDecision,
    });
}
function evaluatePaymentSurface(input, route) {
    if (!input.firstValueCompleted)
        return { visible: false, reason: 'BEFORE_FIRST_VALUE' };
    if (input.memberOfInitialPublicFreeCohort) {
        return { visible: false, reason: 'INITIAL_PUBLIC_COHORT_GRANDFATHERED' };
    }
    if (!POST_VALUE_PAYMENT_PLACEMENTS.has(input.placement)) {
        return { visible: false, reason: 'PLACEMENT_NOT_ALLOWED' };
    }
    if (!input.productionActivated)
        return { visible: false, reason: 'PRODUCTION_DISABLED' };
    if (route === 'payment_disabled')
        return { visible: false, reason: 'PAYMENT_ROUTE_UNAVAILABLE' };
    if (input.paymentProvider !== 'verified')
        return { visible: false, reason: 'PROVIDER_UNVERIFIED' };
    if (!input.catalogVerified)
        return { visible: false, reason: 'CATALOG_UNVERIFIED' };
    return { visible: true, reason: null };
}
/**
 * These are product-policy identifiers, not provider SKU values. A future channel
 * adapter must receive its SKU bindings from a verified provider catalog rather
 * than inventing a sellable identifier in a client bundle.
 */
/**
 * The only candidate paid right is a one-time, non-consumable lifetime
 * entitlement for a future non-grandfathered cohort. A one-off report charge
 * would make a care handoff/export conditional and is intentionally excluded.
 */
export const PAID_PRODUCT_ID_ALLOWLIST = ['lifetime_v1'];
export const PAID_PRODUCT_KIND_BY_ID = {
    lifetime_v1: 'non_consumable_lifetime',
};
export function paidProductKindFor(productId) {
    return PAID_PRODUCT_KIND_BY_ID[productId];
}
/**
 * Provider-independent server-ledger policy. Raw receipts remain inside a future channel adapter;
 * this function only accepts verification facts and never grants on an incomplete or mismatched fact set.
 */
export function reconcilePaidEntitlement(input) {
    const verificationComplete = input.provider === 'verified' && input.skuVerified && input.receiptVerified;
    const freeRights = input.memberOfInitialPublicFreeCohort ? INITIAL_PUBLIC_FREE_COHORT_RIGHTS : [];
    const result = (status, paidEntitlement, grantNewPaidRight, reason) => ({
        status,
        paidEntitlement,
        grantNewPaidRight,
        orderGrantable: verificationComplete && input.orderState === 'succeeded',
        permanentFreeCoreRights: PERMANENT_FREE_CORE_RIGHTS,
        preservedInitialPublicFreeCohortRights: freeRights,
        reason,
    });
    if (input.orderState === 'refunded' || input.orderState === 'charged_back') {
        if (!verificationComplete) {
            return result('reconciliation_failed', input.ledgerState, false, 'PROVIDER_OR_RECEIPT_UNVERIFIED');
        }
        return result(input.ledgerState === 'revoked' ? 'unchanged' : 'revoked', 'revoked', false, 'VERIFIED_REFUND_OR_CHARGEBACK');
    }
    if (!verificationComplete) {
        return result('reconciliation_failed', input.ledgerState, false, 'PROVIDER_OR_RECEIPT_UNVERIFIED');
    }
    if (input.orderState !== 'succeeded') {
        return result('reconciliation_failed', input.ledgerState, false, 'ORDER_NOT_SETTLED');
    }
    if (input.ledgerState === 'granted') {
        return result('unchanged', 'granted', false, 'ALREADY_RECONCILED');
    }
    return result(input.restoreRequested ? 'restored' : 'granted', 'granted', true, input.restoreRequested ? 'VERIFIED_RESTORE' : 'VERIFIED_PURCHASE');
}
export function createFailClosedPaymentAdapter(channel) {
    const reconcile = (input) => reconcilePaidEntitlement({
        ...input,
        provider: 'unverified',
        skuVerified: false,
        receiptVerified: false,
    });
    return Object.freeze({
        enabled: false,
        channel,
        route: digitalCommerceRouteForChannel(channel),
        evaluate: (input) => evaluateMonetizationAccess({
            ...input,
            channel,
            productionActivated: false,
            paymentProvider: 'unverified',
            advertisingProvider: 'disabled',
            catalogVerified: false,
        }),
        reconcile,
        restore: async (input) => reconcile({
            ...input,
            restoreRequested: true,
        }),
    });
}
export function createFailClosedAdvertisingAdapter() {
    return Object.freeze({
        enabled: false,
        evaluate: () => ({
            visible: false,
            reason: 'ADVERTISING_EXCLUDED',
        }),
    });
}
